import { assertNotEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { Icon } from '@festina-life/ui/structure'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { useLocalStorageSearchPageTab } from '../../areas/search/useLocalStorageSearchPageTab'
import { tabNames } from '../../areas/search/utils'
import { useSearchNavigate } from '../../hooks/navigation/entitySearch/useSearchNavigate'
import { useSearch } from '../../hooks/navigation/useSearch'
import { useHook } from '../../lenteHooks'
import { withLifeErrorBoundary } from '../../libs/withLifeErrorBoundary'
import { generalRoutes } from '../../routing/generalRoutes'
import { CommandPalette } from './commandPalette/CommandPalette'
import type { AdvancedSearchParams } from './utility'

const SearchBoxInternal = () => {
  const [query, setQuery] = useState('')
  const searchOptions = useSearch({
    searchTerm: query,
  })
  const tenantId = useHook('routing.tenant.useTenantParam')({
    isOptional: true,
  })
  const navigate = useNavigate()

  const [, setLocalStorageSearchPageTab] = useLocalStorageSearchPageTab()

  const lookupEntity = useSearchNavigate()

  const translate = useTranslate()

  const categoryConfigurations = useHook('routing.search.useSearchCategories')()

  return (
    <CommandPalette
      search={query}
      options={searchOptions}
      categories={categoryConfigurations}
      fallbackOptions={[
        {
          title: translate('search.lookupExternalId', { id: query }),
          apiResponse: undefined,
          hasRequiredComponents: true,
          presentation: (
            <div className="flex items-center gap-2 grow">
              <Icon name="MagnifyingGlass" color="unset" />
              {translate('search.lookupExternalId', { id: query })}
            </div>
          ),
          action: async () => {
            // Must return the Promise (or otherwise await) to provide better feedback.
            const { successfulRequests, navigateInProgress } =
              await lookupEntity(query)
            if (navigateInProgress) {
              return
            }
            // navigate over to search page.
            setLocalStorageSearchPageTab({ name: tabNames.genericLookup })
            assertNotEmpty(tenantId)
            if (successfulRequests.length === 1) {
              await navigate(generalRoutes.error.notFound({ tenantId }))
            } else {
              await navigate(generalRoutes.tenant.search({ tenantId }), {
                state: {
                  _type: 'AdvancedSearchParams',
                  internalOrExternalId: query,
                } satisfies AdvancedSearchParams,
              })
            }
          },
          hasSufficientAccessRights: true,
        },
      ]}
      onChange={(nextValue) => setQuery(nextValue ?? '')}
      placeholder={translate('commandPalette.placeholder')}
    />
  )
}

export const SearchBox = withLifeErrorBoundary(SearchBoxInternal)
